import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { verticalAPI } from '../services/api';
import { useAuth } from './AuthContext';

/**
 * Vertical Engine — frontend consumption.
 *
 * Fetches the tenant's resolved vertical bundle (terminology, navigation, dashboards,
 * role labels, module status) from GET /api/vertical/config and exposes it app-wide.
 * Screens use `term()` for industry terminology and `navigation`/`dashboards` to render
 * themselves per vertical — no hardcoded "Employee"/"Department" strings.
 */

const DEFAULT_BUNDLE = {
  key: 'generic',
  label: 'Generic',
  terminology: {},
  role_labels: {},
  navigation: [],
  dashboards: [],
  workflows: [],
  marketplace: {},
  module_status: {},
  enabled_modules: [],
  upsell_modules: [],
  hidden_modules: [],
};

const VerticalContext = createContext(null);

export const useVertical = () => {
  const ctx = useContext(VerticalContext);
  if (!ctx) throw new Error('useVertical must be used within VerticalProvider');
  return ctx;
};

export const VerticalProvider = ({ children }) => {
  const { user } = useAuth();
  const [bundle, setBundle] = useState(DEFAULT_BUNDLE);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user) {
      setBundle(DEFAULT_BUNDLE);
      return;
    }
    try {
      setLoading(true);
      const res = await verticalAPI.getConfig();
      setBundle({ ...DEFAULT_BUNDLE, ...(res?.data || {}) });
    } catch {
      setBundle(DEFAULT_BUNDLE); // non-fatal: fall back to generic labels
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  /** Industry terminology: term('employee','Employee') -> 'Faculty' in Education. */
  const term = useCallback((key, fallback) => {
    const t = bundle.terminology || {};
    return t[key] || fallback || key;
  }, [bundle]);

  /** Role label override: roleLabel('department_head') -> 'HOD' in Education. */
  const roleLabel = useCallback((key, fallback) => {
    const r = bundle.role_labels || {};
    return r[key] || fallback || key;
  }, [bundle]);

  const value = useMemo(() => ({
    vertical: bundle.key,
    verticalLabel: bundle.label,
    bundle,
    loading,
    refresh,
    term,
    roleLabel,
    navigation: bundle.navigation || [],
    dashboards: bundle.dashboards || [],
    workflows: bundle.workflows || [],
    moduleStatus: bundle.module_status || {},
    recommendedApps: (bundle.marketplace || {}).recommended || [],
  }), [bundle, loading, refresh, term, roleLabel]);

  return <VerticalContext.Provider value={value}>{children}</VerticalContext.Provider>;
};
